import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { siteThemeInput } from '@barbeiropro/contracts';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ImageField } from './image-field';
import type { SiteSettings } from './types';

const HEX = /^#[0-9a-fA-F]{6}$/;
type Values = z.input<typeof siteThemeInput>;

const FONTS = [
  { v: 'classic', name: 'Clássico', desc: 'Letreiro condensado (Big Shoulders Display) com Instrument Sans.', sample: "'Big Shoulders Display',sans-serif", up: true },
  { v: 'modern', name: 'Moderno', desc: 'Instrument Sans em tudo, limpo e direto.', sample: "'Instrument Sans',sans-serif", up: false },
  { v: 'editorial', name: 'Editorial', desc: 'Títulos em Fraunces (serifada) com Instrument Sans.', sample: "'Fraunces',Georgia,serif", up: false },
] as const;

function ColorInput({ id, label, value, onChange, error }: { id: string; label: string; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" aria-label={`${label} (seletor)`} value={HEX.test(value) ? value : '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-11 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1" />
        <Input id={id} value={value} maxLength={7} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} className="tabular max-w-32 font-mono" />
      </div>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function ThemeTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['site', 'settings'], queryFn: () => api.get<SiteSettings>('/site/settings') });
  const form = useForm<Values, unknown, z.output<typeof siteThemeInput>>({ resolver: zodResolver(siteThemeInput), defaultValues: { primary: '#1f1b16', accent: '#b0823c', background: '#f6f1e7', fontPair: 'classic', logoAssetId: null, faviconAssetId: null } });
  const { errors, isSubmitting, isDirty } = form.formState;

  useEffect(() => {
    if (q.data) form.reset({ primary: q.data.primary, accent: q.data.accent, background: q.data.background, fontPair: q.data.fontPair, logoAssetId: q.data.logoAssetId, faviconAssetId: q.data.faviconAssetId });
  }, [q.data, form]);

  if (q.isPending) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;

  const v = form.watch();

  async function submit(values: z.output<typeof siteThemeInput>) {
    try {
      await api.put('/site/settings', values);
      await qc.invalidateQueries({ queryKey: ['site'] });
      toast.success('Tema salvo. As cores e fontes já valem em todo o site.');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <form onSubmit={form.handleSubmit(submit)} className="grid max-w-3xl gap-8" noValidate>
      <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">O tema vale para todo o site e entra no ar assim que você salvar, sem precisar publicar as páginas.</p>
      <section className="grid gap-4 sm:grid-cols-3" aria-label="Cores">
        {(
          [
            ['primary', 'Cor principal (cabeçalho e rodapé)'],
            ['accent', 'Cor de destaque (botões)'],
            ['background', 'Fundo das seções claras'],
          ] as const
        ).map(([k, label]) => (
          <Controller key={k} control={form.control} name={k} render={({ field }) => <ColorInput id={`th-${k}`} label={label} value={field.value} onChange={field.onChange} error={errors[k] && 'Use o formato #RRGGBB.'} />} />
        ))}
      </section>

      <fieldset className="grid gap-3">
        <legend className="mb-1 text-sm font-medium">Par de fontes</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {FONTS.map((f) => (
            <label key={f.v} className={cn('cursor-pointer rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:ring-1 has-[:checked]:ring-primary has-[:focus-visible]:ring-2')}>
              <input type="radio" value={f.v} className="sr-only" {...form.register('fontPair')} />
              <span className="block text-3xl leading-none" style={{ fontFamily: f.sample, textTransform: f.up ? 'uppercase' : 'none', fontWeight: f.v === 'editorial' ? 600 : 800 }}>Corte e barba</span>
              <span className="mt-2 block text-sm font-medium">{f.name}</span>
              <span className="block text-xs text-muted-foreground">{f.desc}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <section aria-label="Prévia das cores" className="overflow-hidden rounded-lg border" style={{ background: v.background }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: v.primary }}>
          <span className="text-lg font-bold text-white mix-blend-difference">Sua barbearia</span>
          <span className="px-3 py-1 text-sm font-semibold" style={{ background: v.accent, color: '#111' }}>Agendar</span>
        </div>
        <p className="p-4 text-sm" style={{ color: '#222' }}>Assim o cabeçalho, os botões e o fundo ficam no site.</p>
      </section>

      <section className="grid gap-5 sm:grid-cols-2" aria-label="Logo e ícone">
        <Controller
          control={form.control}
          name="logoAssetId"
          render={({ field }) => <ImageField label="Logo" withAlt={false} purpose="logo" value={field.value ? { assetId: field.value } : null} onChange={(img) => field.onChange(img?.assetId ?? null)} />}
        />
        <Controller
          control={form.control}
          name="faviconAssetId"
          render={({ field }) => <ImageField label="Ícone da aba (favicon)" withAlt={false} purpose="logo" value={field.value ? { assetId: field.value } : null} onChange={(img) => field.onChange(img?.assetId ?? null)} />}
        />
      </section>

      <div>
        <Button type="submit" disabled={isSubmitting || !isDirty}>Salvar tema</Button>
      </div>
    </form>
  );
}
